import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Phone, Mail, MoreHorizontal, Pencil, Trash2, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { clientStatusLabels, getWhatsAppLink } from '../../lib/utils';

export default function AdminClients() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/clients?${params.toString()}`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, search]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchClients();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchClients]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Cliente removido com sucesso.');
      fetchClients();
    } catch (error) {
      toast.error('Erro ao remover cliente.');
    }
  };

  const handleStatusChange = async (clientId, newStatus) => {
    try {
      await api.patch(`/clients/${clientId}/status?status=${newStatus}`);
      toast.success('Status atualizado!');
      fetchClients();
    } catch (error) {
      toast.error('Erro ao atualizar status.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-slate-100 text-slate-700',
      contacted: 'bg-blue-100 text-blue-700',
      visit_scheduled: 'bg-yellow-100 text-yellow-700',
      proposal: 'bg-orange-100 text-orange-700',
      closed_won: 'bg-green-100 text-green-700',
      closed_lost: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const statuses = ['new', 'contacted', 'visit_scheduled', 'proposal', 'closed_won', 'closed_lost'];

  return (
    <div className="space-y-6" data-testid="admin-clients">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">Clientes</h1>
          <p className="text-slate-500 mt-1">{clients.length} clientes no CRM</p>
        </div>
        <Button asChild className="bg-brand-blue hover:bg-brand-blue-dark">
          <Link to="/admin/clients/new" data-testid="new-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nome, email ou telefone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="client-search"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {statuses.map((s) => (
                  <SelectItem key={s} value={s}>{clientStatusLabels[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline View (Mini Kanban) */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {statuses.map((status) => {
            const statusClients = clients.filter((c) => c.status === status);
            return (
              <div key={status} className="w-72 flex-shrink-0">
                <div className={`${getStatusColor(status)} rounded-t-lg px-4 py-2`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{clientStatusLabels[status]}</span>
                    <Badge variant="secondary" className="bg-white/50">{statusClients.length}</Badge>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-b-lg p-2 min-h-[200px] space-y-2">
                  {statusClients.slice(0, 5).map((client) => (
                    <Card key={client.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/admin/clients/${client.id}`)}>
                      <CardContent className="p-3">
                        <p className="font-medium text-sm text-slate-800 truncate">{client.name}</p>
                        <p className="text-xs text-slate-500 truncate">{client.phone || client.email || 'Sem contato'}</p>
                      </CardContent>
                    </Card>
                  ))}
                  {statusClients.length > 5 && (
                    <p className="text-xs text-slate-500 text-center py-2">
                      +{statusClients.length - 5} mais
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Clients List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
        </div>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-slate-500 mb-4">Nenhum cliente encontrado.</p>
            <Button asChild>
              <Link to="/admin/clients/new">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Primeiro Cliente
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-brand-blue/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-blue font-semibold text-lg">
                      {client.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-slate-800">{client.name}</h3>
                      <Badge className={getStatusColor(client.status)}>
                        {clientStatusLabels[client.status]}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {client.phone}
                        </span>
                      )}
                      {client.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {client.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    {client.phone && (
                      <Button variant="outline" size="icon" asChild className="text-green-600">
                        <a href={getWhatsAppLink(client.phone)} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    <Select 
                      value={client.status} 
                      onValueChange={(v) => handleStatusChange(client.id, v)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((s) => (
                          <SelectItem key={s} value={s}>{clientStatusLabels[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate(`/admin/clients/${client.id}`)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja remover este cliente? Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(client.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
